// The player's VisionMatch comparator: OpenCV loading and caching around the
// shared matcher core (magical-trails-react/vision/visionMatchCore.ts). The
// core is where the pipeline lives — resize, channel choice, CLAHE, ORB, ratio
// test, RANSAC verification — and it is the same code the Node benchmark
// (scripts/visionMatchBench) and the editor's sign-photo check run. This file
// only owns what is browser-specific: getting OpenCV, turning URLs and
// canvases into Mats, and keeping reference and frame features between scan
// ticks so the live loop never recomputes what it already has.
//
// Types only — never bundled at runtime. The actual @techstark/opencv-js
// package (~10 MB) is loaded lazily as a classic <script> pointing at the
// untransformed asset — NOT via a module import, whose promise assimilates
// the package's fake `then` export into an infinite microtask loop that
// permanently locks the main thread (see loadOpenCvScript.ts for the full
// story). Only trails containing a visionMatch page ever trigger that fetch —
// see preloadOpenCV() and its caller in Trail.tsx — and the SW caches it at
// runtime once fetched (see src/sw.ts's opencv-assets-* route; it's
// deliberately excluded from the precache manifest via vite.config.ts's
// globIgnores).
import type * as CvNamespace from '@techstark/opencv-js';
import {
  DEFAULT_MATCHER_OPTIONS,
  chooseChannel,
  effectiveThreshold,
  extractFeatures,
  matchFeatures,
  scoreOf,
  type Channel,
  type CompareImagesFunctionType,
  type CompareImagesResult,
  type CvLike,
  type Features,
  type MatcherOptions,
} from 'magical-trails-react';
import loadOpenCvScript from './loadOpenCvScript';

type Cv = typeof CvNamespace;

export type { CompareImagesResult };
export type CompareImagesOptions = NonNullable<
  Parameters<CompareImagesFunctionType>[2]
>;

/** The tuned pipeline. See the benchmark for the numbers behind it. */
export const MATCHER: Readonly<MatcherOptions> = DEFAULT_MATCHER_OPTIONS;

/** Reference features kept between ticks. Small (about 40 KB each). */
const REFERENCE_CACHE_LIMIT = 8;

// Memoized across calls: preloadOpenCV() (fired from Trail.tsx at trail-load
// time, while online) and every compareImages()/LiveVisionScanner scan tick
// all share this single in-flight load, so the ~10 MB asset is only ever
// fetched once per page session. The loader owns the whole lifecycle —
// script append, wasm-runtime wait (bounded), and failure cleanup — and only
// ever resolves with a READY runtime (cv.Mat present).
let cvPromise: Promise<Cv> | undefined;

function getCv(): Promise<Cv> {
  if (!cvPromise) {
    cvPromise = loadOpenCvScript().catch((err) => {
      // Reset the memo on failure (e.g. offline preload, or a wasm init
      // that never completes) so the NEXT call — either a later
      // preloadOpenCV() retry, or the scan-time compareImages() call —
      // starts a fresh script load instead of forever re-returning this
      // rejected promise. The loader discards its half-initialised global
      // on failure, so the retry really does start clean.
      cvPromise = undefined;
      throw err;
    });
  }
  return cvPromise;
}

/**
 * Kicks off (and memoizes) the OpenCV dynamic import + wasm init without
 * waiting on the result. Call this as soon as it's known a trail will need
 * it — see Trail.tsx, which fires it at trail-load time (while the visitor
 * is presumably online) for any trail containing a visionMatch page — so the
 * SW's CacheFirst route (src/sw.ts) has it cached well before the visitor
 * reaches the scan step, including if they go offline afterwards.
 */
export function preloadOpenCV(): Promise<void> {
  return getCv().then(() => undefined);
}

// ── Image sources → Mats ───────────────────────────────────────────────────

/** A frame or reference as the scanner hands it over. */
export type ImageSource = string | HTMLCanvasElement;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Handle CORS if loading from another domain.
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = src;
  });
}

/** Decodes a source to an RGBA Mat the caller must delete. */
async function readMat(cv: Cv, source: ImageSource): Promise<CvNamespace.Mat> {
  if (typeof source === 'string') {
    return cv.imread(await loadImage(source));
  }
  return cv.imread(source);
}

// ── Reference cache ────────────────────────────────────────────────────────

interface ReferenceEntry {
  features: Features;
  channel: Channel;
}

// Insertion-ordered so the oldest entry is the first key: a cheap LRU.
const referenceCache = new Map<string, Promise<ReferenceEntry>>();

function getReference(cv: Cv, url: string): Promise<ReferenceEntry> {
  const cached = referenceCache.get(url);
  if (cached) {
    // Refresh recency.
    referenceCache.delete(url);
    referenceCache.set(url, cached);
    return cached;
  }
  const entry = (async () => {
    const rgba = await readMat(cv, url);
    try {
      const { channel } = chooseChannel(cv as unknown as CvLike, rgba, MATCHER);
      const features = extractFeatures(
        cv as unknown as CvLike,
        rgba,
        MATCHER.referenceMaxWidth,
        channel,
        MATCHER
      );
      return { features, channel };
    } finally {
      rgba.delete();
    }
  })();
  referenceCache.set(url, entry);
  entry.catch(() => {
    // A reference that failed to load or decode must not poison the cache.
    if (referenceCache.get(url) === entry) referenceCache.delete(url);
  });
  while (referenceCache.size > REFERENCE_CACHE_LIMIT) {
    const oldest = referenceCache.keys().next().value;
    if (oldest === undefined) break;
    const evicted = referenceCache.get(oldest);
    referenceCache.delete(oldest);
    evicted?.then((e) => e.features.delete()).catch(() => undefined);
  }
  return entry;
}

/** Drops every cached reference and the memoised frame (tests, or a trail change). */
export function clearReferenceCache(): void {
  for (const entry of referenceCache.values()) {
    entry.then((e) => e.features.delete()).catch(() => undefined);
  }
  referenceCache.clear();
  releaseFrameMemo();
}

// ── Frame memo ─────────────────────────────────────────────────────────────

// The scanner compares one captured frame against every reference in turn,
// passing the same `frameKey` for the whole tick. Features are detected once
// per channel per key; a new key releases the previous frame's features. The
// memo holds at most one frame (about 100 KB of wasm heap), so nothing is
// released on the scanner's unmount: the next tick, or clearReferenceCache(),
// replaces it. Releasing is safe at those points because a compare takes the
// memoised features and matches against them synchronously; no await sits
// between the two.
let frameMemo: { key: string; byChannel: Map<Channel, Features> } | null = null;

/** Frees the memoised frame features, if any. */
export function releaseFrameMemo(): void {
  if (!frameMemo) return;
  for (const features of frameMemo.byChannel.values()) features.delete();
  frameMemo = null;
}

async function getFrameFeatures(
  cv: Cv,
  source: ImageSource,
  channel: Channel,
  frameKey: string | undefined
): Promise<{ features: Features; owned: boolean }> {
  if (frameKey !== undefined) {
    if (frameMemo && frameMemo.key !== frameKey) releaseFrameMemo();
    const hit = frameMemo?.byChannel.get(channel);
    if (hit) return { features: hit, owned: false };
    // Claim the key BEFORE the decode below awaits. With two ticks in
    // flight (key A decoding, key B arriving), B evicts A's empty claim and
    // takes the memo; when A's decode lands it finds a foreign key and keeps
    // its features to itself. The memo therefore always belongs to the
    // newest tick, and the next call for B's key gets a hit instead of
    // finding a stale A frame it has to release and redo.
    if (!frameMemo) frameMemo = { key: frameKey, byChannel: new Map() };
  }
  const rgba = await readMat(cv, source);
  let features: Features;
  try {
    features = extractFeatures(
      cv as unknown as CvLike,
      rgba,
      MATCHER.frameMaxWidth,
      channel,
      MATCHER
    );
  } finally {
    rgba.delete();
  }
  if (frameKey === undefined) return { features, owned: true };
  // The decode above awaited. If a newer tick claimed the memo meanwhile
  // (or clearReferenceCache() emptied it), or a concurrent call for this
  // same key already filled this channel, these features belong to this
  // call alone: used once and released by the caller, never stored over
  // someone else's.
  if (!frameMemo || frameMemo.key !== frameKey)
    return { features, owned: true };
  if (frameMemo.byChannel.has(channel)) return { features, owned: true };
  frameMemo.byChannel.set(channel, features);
  return { features, owned: false };
}

// ── The comparator ─────────────────────────────────────────────────────────

/**
 * Compare a live frame against a reference image and decide whether the
 * frame shows the reference's sign (or object).
 *
 * @param frame - The captured frame: a canvas, or a URL / data URL.
 * @param reference - The reference image URL (page.visionMatchImages[i]).
 * @param options - `matchThreshold` (default 15; floors at the pipeline's
 *   minimum inlier count), `wantCorrespondences` for the hidden-message
 *   overlay, and `frameKey` so several references share one frame's features.
 * @returns `match`, the `score` compared against the threshold (verified
 *   inliers), and when asked for and verified, the matched pairs plus the
 *   reference→frame homography in full-size pixels.
 */
export async function compareImages(
  frame: ImageSource,
  reference: string,
  options?: CompareImagesOptions
): Promise<CompareImagesResult> {
  const {
    matchThreshold = 15,
    wantCorrespondences = false,
    frameKey,
  } = options ?? {};
  const cv = await getCv();
  const ref = await getReference(cv, reference);
  const { features, owned } = await getFrameFeatures(
    cv,
    frame,
    ref.channel,
    frameKey
  );
  try {
    const outcome = matchFeatures(
      cv as unknown as CvLike,
      ref.features,
      features,
      MATCHER
    );
    const score = scoreOf(outcome, MATCHER);
    const match = score >= effectiveThreshold(matchThreshold, MATCHER);
    if (!wantCorrespondences || !outcome.verified || !outcome.homography) {
      return { match, score };
    }
    return {
      match,
      score,
      homography: outcome.homography,
      correspondences: {
        reference: outcome.pairs.reference,
        frame: outcome.pairs.frame,
        referenceWidth: ref.features.fullWidth,
        referenceHeight: ref.features.fullHeight,
        frameWidth: features.fullWidth,
        frameHeight: features.fullHeight,
      },
    };
  } finally {
    if (owned) features.delete();
  }
}
